import { useState } from 'react';
import { 
  Box, 
  Typography, 
  Grid, 
  TextField, 
  Switch, 
  FormControlLabel, 
  Paper,
  useTheme,
  alpha
} from '@mui/material';
import AccessTimeIcon from '@mui/icons-material/AccessTime';

interface Schedule {
  id?: string;
  day_of_week: number;
  start_time: string;
  end_time: string;
  interval_minutes: number;
  is_active: boolean;
}

interface ScheduleTabProps {
  schedules: Schedule[];
  setSchedules: (schedules: Schedule[]) => void;
}

const DAYS_OF_WEEK = [
  'Domingo',
  'Segunda-feira', 
  'Terça-feira',
  'Quarta-feira',
  'Quinta-feira',
  'Sexta-feira',
  'Sábado'
];

export default function ScheduleTab({ schedules, setSchedules }: ScheduleTabProps) {
  const theme = useTheme();
  
  const updateSchedule = (dayOfWeek: number, field: string, value: any) => {
    const existingScheduleIndex = schedules.findIndex(s => s.day_of_week === dayOfWeek);
    
    if (existingScheduleIndex >= 0) {
      const updatedSchedules = [...schedules];
      updatedSchedules[existingScheduleIndex] = {
        ...updatedSchedules[existingScheduleIndex],
        [field]: value
      };
      setSchedules(updatedSchedules);
    } else {
      const newSchedule: Schedule = {
        day_of_week: dayOfWeek,
        start_time: '06:00',
        end_time: '23:00',
        interval_minutes: 60,
        is_active: true,
        [field]: value
      };
      setSchedules([...schedules, newSchedule]);
    }
  };

  const updateMultipleFields = (dayOfWeek: number, updates: Partial<Schedule>) => {
    const existingScheduleIndex = schedules.findIndex(s => s.day_of_week === dayOfWeek);
    
    if (existingScheduleIndex >= 0) {
      const updatedSchedules = [...schedules];
      updatedSchedules[existingScheduleIndex] = {
        ...updatedSchedules[existingScheduleIndex],
        ...updates
      };
      setSchedules(updatedSchedules);
    } else {
      const newSchedule: Schedule = {
        day_of_week: dayOfWeek,
        start_time: '06:00',
        end_time: '23:00',
        interval_minutes: 60,
        is_active: true,
        ...updates
      };
      setSchedules([...schedules, newSchedule]);
    }
  };

  const getScheduleForDay = (dayOfWeek: number): Schedule => {
    return schedules.find(s => s.day_of_week === dayOfWeek) || {
      day_of_week: dayOfWeek,
      start_time: '06:00',
      end_time: '23:00',
      interval_minutes: 60,
      is_active: true
    };
  };

  const isActiveDay = (schedule: Schedule): boolean => {
    return schedule.is_active && !(schedule.start_time === '00:00' && schedule.end_time === '00:00');
  };

  return (
    <Box sx={{ p: 2 }}>
      <Typography variant="h6" sx={{ mb: 3, display: 'flex', alignItems: 'center', gap: 1, color: theme.palette.text.primary }}>
        <AccessTimeIcon sx={{ color: theme.palette.primary.main }} />
        Horários de Funcionamento
      </Typography>

      <Grid container spacing={2}>
        {DAYS_OF_WEEK.map((dayName, dayIndex) => {
          const schedule = getScheduleForDay(dayIndex);
          const isActive = isActiveDay(schedule);
          
          return (
            <Grid item xs={12} key={dayIndex}>
              <Paper 
                sx={{ 
                  p: 2, 
                  backgroundColor: theme.palette.mode === 'dark' 
                    ? alpha(theme.palette.primary.main, 0.08)
                    : alpha(theme.palette.primary.main, 0.04),
                  border: `1px solid ${theme.palette.divider}`,
                  borderRadius: 2,
                  transition: 'all 0.2s ease-in-out',
                  '&:hover': {
                    backgroundColor: theme.palette.mode === 'dark' 
                      ? alpha(theme.palette.primary.main, 0.12)
                      : alpha(theme.palette.primary.main, 0.06),
                    borderColor: theme.palette.primary.light,
                    transform: 'translateY(-1px)',
                    boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.15)}`
                  }
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                  <Typography 
                    fontWeight={600} 
                    color={theme.palette.text.primary}
                    sx={{ fontSize: 16 }}
                  >
                    {dayName}
                  </Typography>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={isActive}
                        onChange={(e) => {
                          if (e.target.checked) {
                            updateMultipleFields(dayIndex, {
                              is_active: true,
                              start_time: '06:00',
                              end_time: '23:00'
                            });
                          } else {
                            updateMultipleFields(dayIndex, {
                              start_time: '00:00',
                              end_time: '00:00',
                              is_active: true 
                            });
                          }
                        }}
                        size="small"
                        color="primary"
                      />
                    }
                    label={
                      <Typography 
                        color={theme.palette.text.primary} 
                        fontWeight={500}
                        sx={{ fontSize: 14 }}
                      >
                        Ativo
                      </Typography>
                    }
                    sx={{ 
                      m: 0,
                      '& .MuiFormControlLabel-label': {
                        color: theme.palette.text.primary
                      }
                    }}
                  />
                </Box>

                {isActive && (
                  <Grid container spacing={2} alignItems="center">
                    <Grid item xs={12} sm={3}>
                      <TextField
                        label="Início"
                        type="time"
                        value={schedule.start_time}
                        onChange={(e) => updateSchedule(dayIndex, 'start_time', e.target.value)}
                        fullWidth
                        size="small"
                        InputLabelProps={{ 
                          shrink: true,
                          sx: { color: theme.palette.text.secondary }
                        }}
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            backgroundColor: theme.palette.background.paper,
                            '&:hover': {
                              '& .MuiOutlinedInput-notchedOutline': {
                                borderColor: theme.palette.primary.main,
                              },
                            },
                            '&.Mui-focused': {
                              '& .MuiOutlinedInput-notchedOutline': {
                                borderColor: theme.palette.primary.main,
                              },
                            },
                          },
                          '& .MuiInputBase-input': {
                            color: theme.palette.text.primary,
                          }
                        }}
                      />
                    </Grid>
                    <Grid item xs={12} sm={3}>
                      <TextField
                        label="Fim"
                        type="time"
                        value={schedule.end_time}
                        onChange={(e) => updateSchedule(dayIndex, 'end_time', e.target.value)}
                        fullWidth
                        size="small"
                        InputLabelProps={{ 
                          shrink: true,
                          sx: { color: theme.palette.text.secondary }
                        }}
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            backgroundColor: theme.palette.background.paper,
                            '&:hover': {
                              '& .MuiOutlinedInput-notchedOutline': {
                                borderColor: theme.palette.primary.main,
                              },
                            },
                            '&.Mui-focused': {
                              '& .MuiOutlinedInput-notchedOutline': {
                                borderColor: theme.palette.primary.main,
                              },
                            },
                          },
                          '& .MuiInputBase-input': {
                            color: theme.palette.text.primary,
                          }
                        }}
                      />
                    </Grid>
                    <Grid item xs={12} sm={3}>
                      <TextField
                        label="Intervalo (min)"
                        type="number"
                        value={schedule.interval_minutes}
                        onChange={(e) => updateSchedule(dayIndex, 'interval_minutes', parseInt(e.target.value) || 60)}
                        fullWidth
                        size="small"
                        inputProps={{ min: 1, max: 240 }}
                        InputLabelProps={{ 
                          sx: { color: theme.palette.text.secondary }
                        }}
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            backgroundColor: theme.palette.background.paper,
                            '&:hover': {
                              '& .MuiOutlinedInput-notchedOutline': {
                                borderColor: theme.palette.primary.main,
                              },
                            },
                            '&.Mui-focused': {
                              '& .MuiOutlinedInput-notchedOutline': {
                                borderColor: theme.palette.primary.main,
                              },
                            },
                          },
                          '& .MuiInputBase-input': {
                            color: theme.palette.text.primary,
                          }
                        }}
                      />
                    </Grid>
                  </Grid>
                )}

                {!isActive && (
                  <Box 
                    sx={{ 
                      textAlign: 'center', 
                      py: 2,
                      backgroundColor: alpha(theme.palette.error.main, 0.05),
                      borderRadius: 1,
                      border: `1px dashed ${alpha(theme.palette.error.main, 0.3)}`
                    }}
                  >
                    <Typography 
                      color={theme.palette.text.secondary} 
                      fontSize={14} 
                      sx={{ 
                        fontStyle: 'italic',
                        fontWeight: 500
                      }}
                    >
                      Fechado neste dia
                    </Typography>
                  </Box>
                )}
              </Paper>
            </Grid>
          );
        })}
      </Grid>
    </Box>
  );
}